const samePoint = (a, b) => a[0] === b[0] && a[1] === b[1];
const getOpenPolygon = (polygon) => {
    if (polygon.length < 2) {
        return polygon;
    }
    const first = polygon[0];
    const last = polygon[polygon.length - 1];
    return samePoint(first, last) ? polygon.slice(0, -1) : polygon;
};
const distance = (a, b) => {
    return Math.hypot(b[0] - a[0], b[1] - a[1]);
};
const asCoordinate2D = (point) => {
    if (point == null || point.length < 2) {
        return null;
    }
    const result = [point[0], point[1]];
    return Number.isFinite(result[0]) && Number.isFinite(result[1])
        ? result
        : null;
};
const RAY_DIRECTIONS = [
    { name: "NEGATIVE_X", vector: [-1, 0] },
    { name: "POSITIVE_X", vector: [1, 0] },
    { name: "NEGATIVE_Y", vector: [0, -1] },
    { name: "POSITIVE_Y", vector: [0, 1] },
];
const cross = (a, b) => a[0] * b[1] - a[1] * b[0];
const subtract = (a, b) => [
    a[0] - b[0],
    a[1] - b[1],
];
const vectorLength = (vector) => Math.hypot(vector[0], vector[1]);
const normalizeVector = (vector) => {
    const length = vectorLength(vector);
    if (length <= Number.EPSILON) {
        return null;
    }
    return [vector[0] / length, vector[1] / length];
};
const dot = (a, b) => a[0] * b[0] + a[1] * b[1];
const rotatePoint = (point, pivot, angleDeg) => {
    const rad = (angleDeg * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);
    const dx = point[0] - pivot[0];
    const dy = point[1] - pivot[1];
    return [pivot[0] + cos * dx - sin * dy, pivot[1] + sin * dx + cos * dy];
};
const toViewerPoint = (point, transform) => {
    const pointInViewerConvention = (transform.flip2dCoordinate ?? true) ? [point[1], point[0]] : point;
    return rotatePoint(pointInViewerConvention, transform.pivot, transform.angleDeg);
};
const fromViewerPoint = (point, transform) => {
    const unrotatedPoint = rotatePoint(point, transform.pivot, -transform.angleDeg);
    return (transform.flip2dCoordinate ?? true)
        ? [unrotatedPoint[1], unrotatedPoint[0]]
        : unrotatedPoint;
};
const toAxisPoint = (point, axisSystem) => {
    const relativePoint = [
        point[0] - axisSystem.origin[0],
        point[1] - axisSystem.origin[1],
    ];
    return [
        dot(relativePoint, axisSystem.xAxis),
        dot(relativePoint, axisSystem.yAxis),
    ];
};
const fromAxisPoint = (point, axisSystem) => [
    axisSystem.origin[0] +
        point[0] * axisSystem.xAxis[0] +
        point[1] * axisSystem.yAxis[0],
    axisSystem.origin[1] +
        point[0] * axisSystem.xAxis[1] +
        point[1] * axisSystem.yAxis[1],
];
const intersectRayWithSegment = (point, direction, edgeStart, edgeEnd) => {
    const segment = subtract(edgeEnd, edgeStart);
    const denominator = cross(direction, segment);
    if (Math.abs(denominator) <= Number.EPSILON) {
        return null;
    }
    const edgeStartRelativeToPoint = subtract(edgeStart, point);
    const rayDistance = cross(edgeStartRelativeToPoint, segment) / denominator;
    const segmentRatio = cross(edgeStartRelativeToPoint, direction) / denominator;
    if (rayDistance <= Number.EPSILON || segmentRatio < 0 || segmentRatio > 1) {
        return null;
    }
    return {
        point: [
            point[0] + direction[0] * rayDistance,
            point[1] + direction[1] * rayDistance,
        ],
        rayDistance,
    };
};
export const buildDimensionLinesFromPointToPolygonSides = (polygon, point) => {
    const openPolygon = getOpenPolygon(polygon);
    if (openPolygon.length < 2) {
        return [];
    }
    return RAY_DIRECTIONS.flatMap((rayDirection) => {
        let closest = null;
        for (let index = 0; index < openPolygon.length; index += 1) {
            const edgeStart = openPolygon[index];
            const edgeEnd = openPolygon[(index + 1) % openPolygon.length];
            const intersection = intersectRayWithSegment(point, rayDirection.vector, edgeStart, edgeEnd);
            if (!intersection) {
                continue;
            }
            if (!closest || intersection.rayDistance < closest.rayDistance) {
                closest = {
                    edgeIndex: index,
                    point: intersection.point,
                    rayDistance: intersection.rayDistance,
                };
            }
        }
        if (!closest) {
            return [];
        }
        return [
            {
                dimensionLine: [point, closest.point],
                edgeIndex: closest.edgeIndex,
                direction: rayDirection.name,
                distance: distance(point, closest.point),
            },
        ];
    });
};
export const buildDimensionLinesFromPointToPolygonsSides = (polygons, point) => {
    return polygons.flatMap((polygon) => {
        const candidates = buildDimensionLinesFromPointToPolygonSides(polygon, point);
        if (candidates.length === 0) {
            return [];
        }
        return candidates;
    });
};
export const buildDimensionLinesFromPointToClosestPolygonSides = buildDimensionLinesFromPointToPolygonsSides;
export const buildDimensionLinesFromPointToPolygonSidesInAxisSystem = (polygon, point, axisSystem) => {
    const axisPoint = toAxisPoint(point, axisSystem);
    const axisPolygon = polygon.map((polygonPoint) => toAxisPoint(polygonPoint, axisSystem));
    return buildDimensionLinesFromPointToPolygonSides(axisPolygon, axisPoint).map((item) => ({
        ...item,
        dimensionLine: [
            fromAxisPoint(item.dimensionLine[0], axisSystem),
            fromAxisPoint(item.dimensionLine[1], axisSystem),
        ],
    }));
};
export const buildDimensionLinesFromPointToPolygonSidesInViewerTransform = (polygon, point, transform) => {
    const viewerPoint = toViewerPoint(point, transform);
    const viewerPolygon = polygon.map((polygonPoint) => toViewerPoint(polygonPoint, transform));
    return buildDimensionLinesFromPointToPolygonSides(viewerPolygon, viewerPoint).map((item) => ({
        ...item,
        dimensionLine: [
            fromViewerPoint(item.dimensionLine[0], transform),
            fromViewerPoint(item.dimensionLine[1], transform),
        ],
    }));
};
const pointIsInsidePolygon = (point, polygon) => {
    if (polygon.length < 3) {
        return false;
    }
    let inside = false;
    for (let index = 0, previousIndex = polygon.length - 1; index < polygon.length; previousIndex = index++) {
        const current = polygon[index];
        const previous = polygon[previousIndex];
        const crossesY = current[1] > point[1] !== previous[1] > point[1];
        if (!crossesY) {
            continue;
        }
        const xAtPointY = ((previous[0] - current[0]) * (point[1] - current[1])) /
            (previous[1] - current[1]) +
            current[0];
        if (point[0] < xAtPointY) {
            inside = !inside;
        }
    }
    return inside;
};
const distanceFromPointToSegment = (point, segmentStart, segmentEnd) => {
    const segment = [
        segmentEnd[0] - segmentStart[0],
        segmentEnd[1] - segmentStart[1],
    ];
    const segmentLengthSq = dot(segment, segment);
    if (segmentLengthSq <= Number.EPSILON) {
        return vectorLength(subtract(point, segmentStart));
    }
    const t = Math.max(0, Math.min(1, dot(subtract(point, segmentStart), segment) / segmentLengthSq));
    const projectedPoint = [
        segmentStart[0] + t * segment[0],
        segmentStart[1] + t * segment[1],
    ];
    return vectorLength(subtract(point, projectedPoint));
};
const getLongestCeilingGridLineAxis = (lines) => {
    let longestAxis = null;
    let longestLength = 0;
    for (const line of lines) {
        const from = asCoordinate2D(line[0]);
        const to = asCoordinate2D(line[1]);
        if (!from || !to) {
            continue;
        }
        const vector = subtract(to, from);
        const length = vectorLength(vector);
        if (length > longestLength) {
            longestLength = length;
            longestAxis = vector;
        }
    }
    return longestAxis ? normalizeVector(longestAxis) : null;
};
const getCeilingGridDistanceToPoint = (point, lines) => {
    return lines.reduce((closestDistance, line) => {
        const from = asCoordinate2D(line[0]);
        const to = asCoordinate2D(line[1]);
        if (!from || !to) {
            return closestDistance;
        }
        return Math.min(closestDistance, distanceFromPointToSegment(point, from, to));
    }, Number.POSITIVE_INFINITY);
};
export const getCeilingGridAxisSystem = (ceilingGrids, point) => {
    const grids = ceilingGrids ?? [];
    if (grids.length === 0) {
        return null;
    }
    const containingGrid = grids.find((grid) => {
        const edgePolygon = grid.edgePolygon
            ?.map(asCoordinate2D)
            .filter((p) => p != null);
        return edgePolygon != null && pointIsInsidePolygon(point, edgePolygon);
    });
    const nearestGrid = containingGrid ??
        grids.reduce((closestGrid, grid) => {
            if (!closestGrid) {
                return grid;
            }
            const distanceToGrid = getCeilingGridDistanceToPoint(point, grid.lines ?? []);
            const closestDistance = getCeilingGridDistanceToPoint(point, closestGrid.lines ?? []);
            return distanceToGrid < closestDistance ? grid : closestGrid;
        }, null);
    if (!nearestGrid) {
        return null;
    }
    const direction = asCoordinate2D(nearestGrid.direction ?? null);
    const xAxis = direction
        ? normalizeVector(direction)
        : getLongestCeilingGridLineAxis(nearestGrid.lines ?? []);
    if (!xAxis) {
        return null;
    }
    const origin = asCoordinate2D(nearestGrid.intersectionPoint ?? null) ??
        asCoordinate2D(nearestGrid.lines?.[0]?.[0]) ??
        point;
    return {
        origin,
        xAxis,
        yAxis: [xAxis[1], -xAxis[0]],
    };
};
export const buildDimensionLinesFromPointToPolygonSidesWithPreferredAxis = (args) => {
    const ceilingGridAxisSystem = getCeilingGridAxisSystem(args.ceilingGrids, args.point);
    if (ceilingGridAxisSystem) {
        return buildDimensionLinesFromPointToPolygonSidesInAxisSystem(args.polygon, args.point, ceilingGridAxisSystem);
    }
    if (args.fallbackViewerTransform) {
        return buildDimensionLinesFromPointToPolygonSidesInViewerTransform(args.polygon, args.point, args.fallbackViewerTransform);
    }
    return buildDimensionLinesFromPointToPolygonSides(args.polygon, args.point);
};
